import { LuSearch, LuUserPlus } from 'react-icons/lu';
import { Button, FormGroup, Input, InputGroup, InputGroupText, Label, Spinner } from 'reactstrap';

import Avatar from 'src/shared/components/ui/Avatar';
import type { BookingClient, BookingForm } from './useBookingForm';

interface ClientPickerProps {
  form: BookingForm;
  /** Called once a client is chosen or registered, so the wizard can move on. */
  onPicked?: (client: BookingClient) => void;
}

/**
 * Who the appointment is for: search by name or phone, tap her, or register
 * her on the spot with the name already typed.
 */
export default function ClientPicker({ form, onPicked }: ClientPickerProps) {
  const pick = (client: BookingClient) => {
    form.pickClient(client);
    onPicked?.(client);
  };

  if (form.client) {
    return (
      <div className="v-picked">
        <Avatar name={form.client.fullName} size={44} />
        <div className="v-picked__text">
          <span className="v-picked__name">{form.client.fullName}</span>
          {form.client.phone && <span className="v-picked__hint">{form.client.phone}</span>}
        </div>
        <Button color="secondary" outline size="sm" onClick={() => form.pickClient(null)}>
          Cambiar
        </Button>
      </div>
    );
  }

  if (form.isLoadingPreselected) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" />
      </div>
    );
  }

  if (form.newClientName !== null) {
    return (
      <div className="v-newclient">
        <p className="fw-semibold mb-3">Nueva clienta</p>
        <FormGroup>
          <Label for="newClientName">Nombre *</Label>
          <Input
            id="newClientName"
            autoComplete="off"
            value={form.newClientName}
            onChange={(e) => form.setNewClientName(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label for="newClientPhone">Teléfono</Label>
          <Input
            id="newClientPhone"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="Opcional"
            value={form.newClientPhone}
            onChange={(e) => form.setNewClientPhone(e.target.value)}
          />
        </FormGroup>
        {form.createClient.isError && (
          <div className="text-danger small mb-2">
            No pudimos registrarla. Revisa los datos; el teléfono puede pertenecer a otra clienta.
          </div>
        )}
        <div className="d-flex gap-2">
          <Button color="link" onClick={() => form.setNewClientName(null)}>
            Volver
          </Button>
          <Button
            color="primary"
            className="flex-grow-1"
            disabled={form.createClient.isPending || form.newClientName.trim() === ''}
            onClick={() => form.registerClient(onPicked)}
          >
            {form.createClient.isPending ? <Spinner size="sm" /> : 'Registrar y continuar'}
          </Button>
        </div>
      </div>
    );
  }

  const results = form.clients.data?.items ?? [];
  const typed = form.search.trim();
  const settled = form.debouncedSearch === form.search;

  return (
    <div>
      <Label for="bookClient" className="visually-hidden">
        Buscar clienta
      </Label>
      <InputGroup>
        <InputGroupText>
          <LuSearch aria-hidden="true" />
        </InputGroupText>
        <Input
          id="bookClient"
          type="search"
          autoComplete="off"
          placeholder="Nombre o teléfono"
          value={form.search}
          onChange={(e) => form.setSearch(e.target.value)}
        />
      </InputGroup>

      <ul className="v-pick-list" aria-label="Clientas">
        {results.map((person) => (
          <li key={person.id}>
            <button
              type="button"
              className="v-pick-list__row"
              onClick={() =>
                pick({ id: person.id, fullName: person.fullName, phone: person.phone })
              }
            >
              <Avatar name={person.fullName} size={40} />
              <span className="v-pick-list__text">
                <span className="v-pick-list__name">{person.fullName}</span>
                <span className="v-pick-list__hint">{person.phone ?? 'Sin teléfono'}</span>
              </span>
            </button>
          </li>
        ))}
        {typed !== '' && (
          <li>
            <button
              type="button"
              className="v-pick-list__row v-pick-list__row--new"
              onClick={form.startNewClient}
            >
              <span className="v-pick-list__icon" aria-hidden="true">
                <LuUserPlus />
              </span>
              <span className="v-pick-list__text">
                <span className="v-pick-list__name">Registrar «{typed}»</span>
                <span className="v-pick-list__hint">
                  {settled && results.length === 0
                    ? 'Ninguna clienta coincide'
                    : 'Si es una clienta nueva'}
                </span>
              </span>
            </button>
          </li>
        )}
      </ul>
      {form.clients.isFetching && <div className="text-muted small mt-2">Buscando…</div>}
    </div>
  );
}
